// =============================================================================
// src/features/explore/explore-g26-derive.ts — Derivacoes PURAS do modo 2026
//   (lider/2o por regiao, linhas do overlay de votos, ranking p/ popup e menu).
//
// Diferente de explore-vote-data (que SINTETIZA uma distribuicao a partir do
//   vencedor/%), aqui tudo sai dos votos OFICIAIS do TSE por candidato. O
//   adaptador `g26Entries` devolve a mesma forma RegionVoteEntry, entao o popup de
//   ranking e as bolhas/calor do overlay funcionam sem mudar de codigo.
//
// Cargo proporcional (deputados): a "cor da regiao" e o PARTIDO mais votado
//   (nominais + legenda), que e o que define cadeira; majoritario: o candidato.
// SRP: sem DOM, sem Leaflet, sem fetch.
// =============================================================================

import type { HexColor, MunicipalityId } from '../../app/types';
import type { G26Apuracao, G26CargoCode, G26Candidato, G26Dataset } from './explore-g26-types';
import type { RegionVoteEntry, VoteRow } from './explore-vote-data';
import { g26PartyColor } from './explore-g26-constants';
import { formatVotos, formatPercent } from './explore-vote-anim';

/** Selecao ativa do modo 2026: dataset carregado + cargo. */
export interface G26Selection {
  readonly ds: G26Dataset;
  readonly cargo: G26CargoCode;
}

/** Nivel geografico de uma apuracao. */
export type G26Level = 'estado' | 'mun' | 'zona';

/** Prefixo dos ids de candidato reais (nao colide com os ids sinteticos `PL#1`). */
const G26_ID_PREFIX = 'g26:';

/** Id de candidato 2026 a partir do indice no registro. */
export function g26CandidateId(index: number): string {
  return `${G26_ID_PREFIX}${index}`;
}

/** Indice do registro a partir do id (`g26:12` -> 12); null se nao for id 2026. */
export function g26IndexOfId(id: string | null): number | null {
  if (!id?.startsWith(G26_ID_PREFIX)) return null;
  const index = Number(id.slice(G26_ID_PREFIX.length));
  return Number.isInteger(index) && index >= 0 ? index : null;
}

/** Apuracao de um cargo numa regiao (null quando a regiao/cargo nao existe). */
export function g26Apuracao(ds: G26Dataset, level: G26Level, id: string, cargo: G26CargoCode): G26Apuracao | null {
  if (level === 'estado') return ds.estado[cargo] ?? null;
  const byCargo = level === 'mun' ? ds.municipios[id] : ds.zonas[id];
  return byCargo?.[cargo] ?? null;
}

/** Percentual (0..100) de votos sobre um total (0 quando total 0). */
export function g26Pct(votos: number, total: number): number {
  return total > 0 ? (votos / total) * 100 : 0;
}

/** Candidato do registro pelo indice (null se fora do registro). */
export function g26Candidate(ds: G26Dataset, index: number): G26Candidato | null {
  return ds.candidatos[index] ?? null;
}

/**
 * Ranking oficial de uma apuracao na forma do overlay (RegionVoteEntry): todos os
 * candidatos com voto, posicao 1..N, % sobre os validos (padrao do TSE).
 *
 * @example g26Entries(ds, apu)[0].cand.nome // 'OMAR AZIZ'
 */
export function g26Entries(ds: G26Dataset, apu: G26Apuracao): readonly RegionVoteEntry[] {
  return apu.candidatos.map(([index, votos], k) => {
    const cand = ds.candidatos[index];
    return {
      cand: { id: g26CandidateId(index), party: cand?.partido ?? '?', nome: cand?.nome ?? `#${index}`, ordem: index },
      votos,
      pct: g26Pct(votos, apu.validos),
      posicao: k + 1,
    };
  });
}

/** Partido (sigla, votos) na posicao `rank` (0 = lider) de uma apuracao. */
function partyAt(ds: G26Dataset, apu: G26Apuracao, rank: number): { sigla: string; votos: number } | null {
  if (apu.partidos) {
    const row = apu.partidos[rank];
    return row ? { sigla: row[0], votos: row[1] + row[2] } : null;
  }
  const pair = apu.candidatos[rank];
  const cand = pair ? ds.candidatos[pair[0]] : undefined;
  return pair && cand ? { sigla: cand.partido, votos: pair[1] } : null;
}

/** Partido lider da regiao (candidato vencedor; partido mais votado se proporcional). */
export function g26LeaderParty(ds: G26Dataset, apu: G26Apuracao): string | null {
  return partyAt(ds, apu, 0)?.sigla ?? null;
}

/** Partido em 2º na regiao (mesma regra do lider). */
export function g26RunnerUpParty(ds: G26Dataset, apu: G26Apuracao): string | null {
  return partyAt(ds, apu, 1)?.sigla ?? null;
}

/** % do lider sobre os validos (competitividade). */
export function g26LeaderPct(ds: G26Dataset, apu: G26Apuracao): number {
  return g26Pct(partyAt(ds, apu, 0)?.votos ?? 0, apu.validos);
}

/** Cor da regiao pelo lider (ou pelo 2º) — null sem apuracao. */
export function g26RegionColor(sel: G26Selection, munId: MunicipalityId, which: 'p' | 's'): HexColor | null {
  const apu = g26Apuracao(sel.ds, 'mun', munId, sel.cargo);
  if (!apu) return null;
  const sigla = which === 'p' ? g26LeaderParty(sel.ds, apu) : g26RunnerUpParty(sel.ds, apu);
  return sigla ? g26PartyColor(sigla) : null;
}

/**
 * Linhas do overlay (bolhas/calor) por municipio: o candidato escolhido (id `g26:`)
 * ou o vencedor de cada cidade. Pula cidade onde o candidato nao teve voto.
 */
export function deriveG26VoteRows(sel: G26Selection, candidateId: string | null): ReadonlyMap<MunicipalityId, VoteRow> {
  const wanted = g26IndexOfId(candidateId);
  const rows = new Map<MunicipalityId, VoteRow>();
  for (const munId of Object.keys(sel.ds.municipios)) {
    const apu = g26Apuracao(sel.ds, 'mun', munId, sel.cargo);
    if (!apu) continue;
    const entry = g26Entries(sel.ds, apu).find((e) => (wanted === null ? e.posicao === 1 : e.cand.ordem === wanted));
    if (entry && entry.votos > 0) rows.set(munId, { votosAbsolutos: entry.votos, percentual: entry.pct, ranking: entry.posicao, party: entry.cand.party });
  }
  return rows;
}

/** Candidato do cargo com votos/% no AM e a colocacao estadual. */
export interface G26RankedCandidate {
  readonly index: number;
  readonly cand: G26Candidato;
  readonly votos: number;
  readonly pct: number;
  readonly posicao: number;
}

/** Candidatos do cargo pela ordem oficial do estado (inclui quem teve 0 voto, no fim). */
export function g26RankedCandidates(sel: G26Selection): readonly G26RankedCandidate[] {
  const estado = sel.ds.estado[sel.cargo];
  const validos = estado?.validos ?? 0;
  const ordered = sel.ds.candidatos
    .map((cand, index) => ({ cand, index }))
    .filter(({ cand }) => cand.cargo === sel.cargo)
    .sort((a, b) => b.cand.votos - a.cand.votos || a.cand.nome.localeCompare(b.cand.nome));
  return ordered.map(({ cand, index }, k) => ({ index, cand, votos: cand.votos, pct: g26Pct(cand.votos, validos), posicao: k + 1 }));
}

/** Situacao do TSE em forma curta p/ rotulo ('ELEITO POR QP' -> 'eleito (QP)'). */
export function g26SituacaoLabel(situacao: string): string {
  if (situacao === 'ELEITO POR QP') return 'eleito (QP)';
  if (situacao === 'ELEITO POR MÉDIA') return 'eleito (média)';
  if (situacao.startsWith('#')) return '';
  return situacao.toLowerCase();
}

/** Rotulo do menu: `1º OMAR AZIZ (PSD) · 841.846 votos · 40,63% · 2º turno`. */
export function g26CandidateLabel(r: G26RankedCandidate): string {
  const situacao = g26SituacaoLabel(r.cand.situacao);
  const tail = situacao ? ` · ${situacao}` : '';
  return `${r.posicao}º ${r.cand.nome} (${r.cand.partido}) · ${formatVotos(r.votos)} votos · ${formatPercent(r.pct, 2)}${tail}`;
}

/** Partidos lideres (ou 2os) presentes nos municipios, ordenados pelos votos no estado. */
export function g26PartiesPresent(sel: G26Selection, which: 'p' | 's'): readonly string[] {
  const present = new Set<string>();
  for (const munId of Object.keys(sel.ds.municipios)) {
    const apu = g26Apuracao(sel.ds, 'mun', munId, sel.cargo);
    const sigla = apu ? (which === 'p' ? g26LeaderParty(sel.ds, apu) : g26RunnerUpParty(sel.ds, apu)) : null;
    if (sigla) present.add(sigla);
  }
  return g26PartiesByStateVotes(sel).filter((sigla) => present.has(sigla));
}

/** Todas as siglas do cargo, ordenadas pelos votos no estado (menu de Partido). */
export function g26PartiesByStateVotes(sel: G26Selection): readonly string[] {
  const totals = new Map<string, number>();
  for (const cand of sel.ds.candidatos) {
    if (cand.cargo === sel.cargo) totals.set(cand.partido, (totals.get(cand.partido) ?? 0) + cand.votos);
  }
  for (const [sigla, legenda] of sel.ds.estado[sel.cargo]?.partidos ?? []) totals.set(sigla, (totals.get(sigla) ?? 0) + legenda);
  return [...totals.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([sigla]) => sigla);
}
